import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { buildRecordingFile } from '../../../Components/VoiceRecording/buildRecording'
import type { VoiceRecording } from '../../../Components/VoiceRecording/TextAreaWithVoiceRecordings'
import type { RecordType } from '../../../Components/VoiceRecording/types'

type StoredVoiceRecording = VoiceRecording & {
  blobUrl: string
  file: File
}

export const useVoiceRecordings = () => {
  const [recordings, setRecordings] = useState<StoredVoiceRecording[]>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  // Ref tracking current recordings so the unmount cleanup below can revoke blob
  // URLs without closing over a stale initial value.
  const recordingsRef = useRef<StoredVoiceRecording[]>([])
  useEffect(() => {
    recordingsRef.current = recordings
  }, [recordings])

  const openModal = useCallback(() => setIsModalOpen(true), [])
  const closeModal = useCallback(() => setIsModalOpen(false), [])

  const handleRecordingSaved = useCallback((recording: RecordType) => {
    const file = buildRecordingFile(recording)
    const blobUrl = URL.createObjectURL(recording.data)
    setRecordings(prev => [
      ...prev,
      {
        id: crypto.randomUUID(),
        durationMs: recording.duration,
        file,
        blobUrl,
      },
    ])
    setIsModalOpen(false)
  }, [])

  const handleRemove = useCallback((recordingId: string) => {
    setRecordings(prev => {
      const removed = prev.find(r => r.id === recordingId)
      if (removed) {
        URL.revokeObjectURL(removed.blobUrl)
      }
      return prev.filter(r => r.id !== recordingId)
    })
  }, [])

  const reset = useCallback(() => {
    setRecordings(prev => {
      for (const r of prev) {
        URL.revokeObjectURL(r.blobUrl)
      }
      return []
    })
  }, [])

  useEffect(
    () => () => {
      for (const r of recordingsRef.current) {
        URL.revokeObjectURL(r.blobUrl)
      }
    },
    []
  )

  const files = useMemo(() => recordings.map(r => r.file), [recordings])

  return {
    recordings,
    files,
    isModalOpen,
    openModal,
    closeModal,
    handleRecordingSaved,
    handleRemove,
    reset,
  }
}
